import React, { useState, useMemo } from 'react';
import { X, Calculator, Euro, Check, Star } from 'lucide-react';
import { SCORES_DATABASE, getScoreById } from '../data/scoresDatabase';
import { toast } from 'sonner';

// Get full score data from database
const getFullScoreData = (scoreId) => {
  for (const [catKey, category] of Object.entries(SCORES_DATABASE)) {
    const score = category.scores.find(s => s.id === scoreId);
    if (score) {
      return { ...score, categoryKey: catKey, categoryName: category.name };
    }
  }
  return null;
};

export const ScoreCalculatorModal = ({ isOpen, onClose, scoreId }) => {
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);

  const score = useMemo(() => getFullScoreData(scoreId), [scoreId]);

  // Reset state when modal opens with new score
  React.useEffect(() => {
    if (isOpen) {
      setAnswers({});
      setResult(null);
    }
  }, [isOpen, scoreId]);

  if (!isOpen || !score) return null;

  const calculateScore = () => {
    let totalScore = 0;
    let subscores = {};

    // Different calculation types based on score structure
    if (score.questions) {
      if (score.subscales) {
        score.subscales.forEach(sub => {
          subscores[sub] = 0;
        });
        score.questions.forEach((q, idx) => {
          const answer = answers[`q${idx}`];
          if (answer !== undefined) {
            if (q.subscale) {
              subscores[q.subscale] += answer;
            }
            totalScore += answer;
          }
        });
      } else {
        score.questions.forEach((q, idx) => {
          const answer = answers[`q${idx}`];
          if (answer !== undefined) {
            totalScore += typeof answer === 'boolean' ? (answer ? 1 : 0) : answer;
          }
        });
      }
    } else if (score.fields) {
      if (score.calculate) {
        totalScore = score.calculate(answers);
      } else {
        score.fields.forEach(field => {
          if (field.points && answers[field.id]) {
            totalScore += field.points;
          }
        });
      }
    } else if (score.sections) {
      Object.values(answers).forEach(val => {
        if (typeof val === 'number') totalScore += val;
      });
    } else if (score.activities) {
      score.activities.forEach((act, idx) => {
        if (answers[`act${idx}`] !== undefined) {
          totalScore += answers[`act${idx}`];
        }
      });
    } else if (score.options) {
      totalScore = answers.score || 0;
    }

    // Interpretation
    let interpretation;
    if (score.subscales && Object.keys(subscores).length > 0) {
      interpretation = score.interpretation(subscores);
    } else if (score.id === 'score2') {
      interpretation = score.interpretation(totalScore, answers.age);
    } else if (score.id === 'chadsvasc') {
      interpretation = score.interpretation(totalScore, answers.femme ? 'femme' : 'homme');
    } else {
      interpretation = score.interpretation(totalScore);
    }

    setResult({
      score: totalScore,
      subscores,
      interpretation,
      cotation: score.cotation
    });

    toast.success(`Score calculé: ${totalScore}`);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div 
        className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-700 bg-slate-800/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center">
              <Calculator className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">{score.name}</h2>
              <p className="text-sm text-slate-400">{score.fullName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[calc(85vh-140px)]">
          {/* Cotation Info */}
          {score.cotation && (
            <div className="mb-4 p-3 bg-green-900/20 border border-green-500/30 rounded-xl">
              <div className="flex items-center gap-2 text-green-400 text-sm font-medium">
                <Euro className="w-4 h-4" />
                <span className="font-mono">{score.cotation.code}</span> - {score.cotation.libelle}
                <span className="ml-auto font-semibold">{score.cotation.tarif}€</span>
              </div>
            </div>
          )}

          {/* Form Fields */}
          <div className="space-y-3">
            {/* Standard Questions */}
            {score.questions && !score.subscales && (
              score.questions.map((q, idx) => (
                <div key={idx} className="p-3 bg-slate-800/50 rounded-xl">
                  <p className="text-white text-sm mb-2">{typeof q === 'string' ? q : q.text}</p>
                  <div className="flex flex-wrap gap-2">
                    {(score.options || q.options || [{ label: "Oui", value: 1 }, { label: "Non", value: 0 }]).map((opt, optIdx) => (
                      <button
                        key={optIdx}
                        onClick={() => setAnswers({ ...answers, [`q${idx}`]: opt.value })}
                        className={`px-3 py-1.5 rounded-lg text-xs transition-colors ${
                          answers[`q${idx}`] === opt.value
                            ? 'bg-indigo-600 text-white'
                            : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))
            )}

            {/* HAD-style questions with subscales */}
            {score.questions && score.subscales && (
              score.questions.map((q, idx) => (
                <div key={idx} className="p-3 bg-slate-800/50 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-xs px-2 py-0.5 rounded ${q.subscale === 'anxiete' ? 'bg-purple-500/30 text-purple-300' : 'bg-blue-500/30 text-blue-300'}`}>
                      {q.subscale === 'anxiete' ? 'A' : 'D'}
                    </span>
                    <p className="text-white text-sm">{q.text}</p>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {q.options.map((opt, optIdx) => (
                      <button
                        key={optIdx}
                        onClick={() => setAnswers({ ...answers, [`q${idx}`]: opt.value })}
                        className={`px-2 py-1 rounded text-xs transition-colors ${
                          answers[`q${idx}`] === opt.value
                            ? 'bg-indigo-600 text-white'
                            : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))
            )}

            {/* Fields (SCORE2, CHA2DS2-VASc, etc.) */}
            {score.fields && (
              score.fields.map((field) => (
                <div key={field.id} className="p-3 bg-slate-800/50 rounded-xl">
                  <label className="text-white text-sm block mb-2">{field.label}</label>
                  {field.type === 'number' && (
                    <input
                      type="number"
                      min={field.min}
                      max={field.max}
                      step={field.step || 1}
                      value={answers[field.id] || ''}
                      onChange={(e) => setAnswers({ ...answers, [field.id]: parseFloat(e.target.value) })}
                      className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm"
                    />
                  )}
                  {field.type === 'boolean' && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => setAnswers({ ...answers, [field.id]: true })}
                        className={`px-4 py-1.5 rounded-lg text-sm ${answers[field.id] === true ? 'bg-indigo-600 text-white' : 'bg-slate-700 text-slate-300'}`}
                      >
                        Oui
                      </button>
                      <button
                        onClick={() => setAnswers({ ...answers, [field.id]: false })}
                        className={`px-4 py-1.5 rounded-lg text-sm ${answers[field.id] === false ? 'bg-indigo-600 text-white' : 'bg-slate-700 text-slate-300'}`}
                      >
                        Non
                      </button>
                    </div>
                  )}
                  {field.type === 'select' && (
                    <select
                      value={answers[field.id] || ''}
                      onChange={(e) => setAnswers({ ...answers, [field.id]: e.target.value })}
                      className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm"
                    >
                      <option value="">Sélectionner...</option>
                      {field.options.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  )}
                  {field.type === 'range' && (
                    <div>
                      <input
                        type="range"
                        min={field.min}
                        max={field.max}
                        value={answers[field.id] || field.min}
                        onChange={(e) => setAnswers({ ...answers, [field.id]: parseInt(e.target.value) })}
                        className="w-full"
                      />
                      <div className="flex justify-between text-slate-400 text-xs mt-1">
                        <span>{field.min}</span>
                        <span className="text-lg font-bold text-white">{answers[field.id] || field.min}</span>
                        <span>{field.max}</span>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}

            {/* Sections (MMSE, Glasgow) */}
            {score.sections && (
              score.sections.map((section, sIdx) => (
                <div key={sIdx} className="p-3 bg-slate-800/50 rounded-xl">
                  <h4 className="text-white text-sm font-medium mb-2">{section.name} {section.max && `(/${section.max})`}</h4>
                  {section.options ? (
                    <div className="flex flex-wrap gap-1">
                      {section.options.map((opt) => (
                        <button
                          key={opt.value}
                          onClick={() => setAnswers({ ...answers, [`s${sIdx}`]: opt.value })}
                          className={`px-2 py-1 rounded text-xs ${
                            answers[`s${sIdx}`] === opt.value ? 'bg-indigo-600 text-white' : 'bg-slate-700 text-slate-300'
                          }`}
                        >
                          {opt.label} ({opt.value})
                        </button>
                      ))}
                    </div>
                  ) : (
                    <input
                      type="number"
                      min={0}
                      max={section.max}
                      value={answers[`s${sIdx}`] || ''}
                      onChange={(e) => setAnswers({ ...answers, [`s${sIdx}`]: parseInt(e.target.value) })}
                      className="w-16 px-2 py-1 bg-slate-900 border border-slate-700 rounded text-white text-sm"
                      placeholder={`/${section.max}`}
                    />
                  )}
                </div>
              ))
            )}

            {/* Activities (ADL) */}
            {score.activities && (
              score.activities.map((act, idx) => (
                <div key={idx} className="p-3 bg-slate-800/50 rounded-xl">
                  <p className="text-white text-sm mb-2">{act.name}</p>
                  <div className="flex gap-2">
                    {act.options.map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => setAnswers({ ...answers, [`act${idx}`]: opt.value })}
                        className={`px-3 py-1.5 rounded-lg text-xs ${
                          answers[`act${idx}`] === opt.value ? 'bg-indigo-600 text-white' : 'bg-slate-700 text-slate-300'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))
            )}

            {/* Single option scores (mMRC) */}
            {score.options && !score.questions && (
              <div className="space-y-2">
                {score.options.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setAnswers({ ...answers, score: opt.value })}
                    className={`w-full p-3 rounded-xl text-left text-sm transition-colors ${
                      answers.score === opt.value ? 'bg-indigo-600 text-white' : 'bg-slate-800/50 text-slate-300 hover:bg-slate-700'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Result */}
          {result && (
            <div className="mt-4 p-4 bg-slate-800 rounded-xl border border-slate-600">
              <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                <Check className="w-5 h-5 text-green-400" />
                Résultat
              </h3>
              
              <div className="text-center mb-4">
                <div className="text-4xl font-bold text-white mb-1">
                  {typeof result.score === 'number' ? result.score : JSON.stringify(result.score)}
                </div>
                {result.subscores && Object.keys(result.subscores).length > 0 && (
                  <div className="flex justify-center gap-3 text-xs">
                    {Object.entries(result.subscores).map(([key, val]) => (
                      <span key={key} className="text-slate-400">
                        {key}: <span className="text-white font-semibold">{val}</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {result.interpretation && (
                <div className="space-y-2">
                  {result.interpretation.anxiete && result.interpretation.depression ? (
                    <>
                      <p className={`text-sm ${result.interpretation.anxiete.color}`}>{result.interpretation.anxiete.text}</p>
                      <p className={`text-sm ${result.interpretation.depression.color}`}>{result.interpretation.depression.text}</p>
                      <p className="text-slate-300 text-sm">→ {result.interpretation.action}</p>
                    </>
                  ) : (
                    <>
                      <p className={`font-semibold ${result.interpretation.color}`}>
                        {result.interpretation.text}
                      </p>
                      <p className="text-slate-300 text-sm bg-slate-900 p-2 rounded-lg">
                        <span className="text-indigo-400 font-medium">CAT:</span> {result.interpretation.action}
                      </p>
                    </>
                  )}
                </div>
              )}

              {result.cotation && (
                <div className="mt-3 p-2 bg-green-900/30 border border-green-500/30 rounded-lg">
                  <p className="text-green-400 text-sm flex items-center gap-2">
                    <Euro className="w-4 h-4" />
                    Coter: {result.cotation.code} ({result.cotation.tarif}€)
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-700 bg-slate-800/50">
          <button
            onClick={calculateScore}
            className="w-full py-3 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-semibold transition-all"
            data-testid="modal-calculate-btn"
          >
            <Calculator className="inline w-4 h-4 mr-2" />
            Calculer
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScoreCalculatorModal;
